import React, { useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI } from '../../services/api';
import { Button } from '../ui';
import { handleApiError, showToast } from '../../utils/helpers';
import { X, Upload, Download, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Card } from '../ui';

const ImportPostsModal = ({ onClose, onSuccess }) => {
  const { isRTL, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

      // Check file type
      if (!selectedFile.name.endsWith('.csv')) {
        showToast.error(t('pleaseSelectCSVFileOnly'));
        return;
      }

    setFile(selectedFile);

    try {
      // Read file content
      const text = await selectedFile.text();
      
      // Parse CSV
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        showToast.error(t('fileIsEmptyOrInvalid'));
        setFile(null);
        return;
      }

      // Parse header
      const headers = parseCSVLine(lines[0]);
      
      // Parse rows
      const rows = [];
      for (let i = 1; i < Math.min(lines.length, 11); i++) { // Preview first 10 rows
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header.trim()] = values[index]?.trim() || '';
        });
        rows.push(row);
      }

      setPreviewData({
        headers,
        rows,
        totalRows: lines.length - 1,
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      showToast.error(t('failedToReadFile'));
      setFile(null);
    }
  };

  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current); // Push last value

    return values;
  };

  const handleImport = async () => {
    if (!file) {
      showToast.error(isRTL ? 'يرجى اختيار ملف' : 'Please select a file');
      return;
    }

    try {
      setLoading(true);
      
      // Read full file
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        showToast.error(isRTL ? 'الملف فارغ' : 'File is empty');
        return;
      }

      // Parse CSV
      const headers = parseCSVLine(lines[0]);
      const rows = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header.trim()] = values[index]?.trim() || '';
        });
        rows.push(row);
      }

      // Send to backend
      const response = await postsAPI.importPosts(rows);

      setImportResults(response.data.results);
      showToast.success(response.data.message);
      
      // If all succeeded or any succeeded, refresh the list
      if (response.data.results.success.length > 0) {
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Import error:', error);
      showToast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleFile = () => {
    const sampleData = [
      ['Serial Number', 'Title', 'Description', 'Caption', 'Platform', 'Content Type', 'Status', 'Image URL', 'Client Name'],
      ['1', 'Sample Post 1', 'This is a sample description', 'Sample caption for Instagram', 'instagram', 'image', 'pending', 'https://example.com/image1.jpg', 'Sample Client'],
      ['2', 'Sample Post 2', 'Another description', 'Another caption', 'facebook', 'video', 'pending', 'https://example.com/video1.mp4', 'Sample Client'],
      ['3', 'Sample Post 3', '', '', 'twitter', 'image', 'draft', '', 'Sample Client'],
    ];

    const csvContent = sampleData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'posts-import-sample.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('importPostsFromCSV')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {!importResults ? (
            <>
              {/* File Upload */}
              <div className="mb-6">
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('selectCSVFile')}
                </label>
                <div className="flex gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1"
                  >
                    <Upload className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t('chooseFile')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={downloadSampleFile}
                  >
                    <Download className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t('downloadSample')}
                  </Button>
                </div>
                {file && (
                  <p className={`text-sm text-gray-600 mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('selectedFile')} {file.name}
                  </p>
                )}
              </div>

              {/* Preview */}
              {previewData && (
                <Card className="p-4 mb-6">
                  <div className={`mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {t('dataPreview')} ({previewData.totalRows} {isRTL ? 'صف' : 'rows'})
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('showingFirstRowsOnly')}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          {previewData.headers.map((header, idx) => (
                            <th key={idx} className={`px-3 py-2 border-b border-gray-200 text-left font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className="border-b border-gray-100">
                            {previewData.headers.map((header, colIdx) => (
                              <td key={colIdx} className={`px-3 py-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {row[header] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Instructions */}
              <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <h4 className="font-semibold text-blue-900 mb-2">
                      {t('importantNotes')}
                    </h4>
                    <ul className={`text-sm text-blue-800 space-y-1 ${isRTL ? 'list-disc list-inside' : 'list-disc list-inside'}`}>
                      <li>{t('requiredFieldsNote')}</li>
                      <li>{t('platformMustBe')}</li>
                      <li>{t('contentTypeMustBe')}</li>
                      <li>{t('serialNumberNote')}</li>
                      <li>{t('clientNameMustMatch')}</li>
                    </ul>
                  </div>
                </div>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleImport}
                  loading={loading}
                  disabled={!file || loading}
                  className="flex-1"
                >
                  {t('importPostsButton')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1"
                >
                  {t('cancelButton')}
                </Button>
              </div>
            </>
          ) : (
            /* Import Results */
            <div>
              <div className="mb-6">
                <h3 className={`text-xl font-semibold text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('importResults')}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card className="p-4 bg-green-50 border-green-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold text-green-900">{importResults.success.length}</p>
                        <p className="text-sm text-green-700">{t('succeeded')}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-red-50 border-red-200">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="text-2xl font-bold text-red-900">{importResults.failed.length}</p>
                        <p className="text-sm text-red-700">{t('failed')}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-yellow-50 border-yellow-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <div>
                        <p className="text-2xl font-bold text-yellow-900">{importResults.skipped.length}</p>
                        <p className="text-sm text-yellow-700">{t('skipped')}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Failed Rows */}
                {importResults.failed.length > 0 && (
                  <div className="mb-4">
                    <h4 className={`font-semibold text-red-900 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('failedRows')}
                    </h4>
                    <div className="max-h-48 overflow-y-auto border border-red-200 rounded">
                      {importResults.failed.map((item, idx) => (
                        <div key={idx} className={`p-2 border-b border-red-100 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <p className="text-sm font-medium text-red-900">
                            {isRTL ? `صف ${item.row}:` : `Row ${item.row}:`} {item.reason}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skipped Rows */}
                {importResults.skipped.length > 0 && (
                  <div className="mb-4">
                    <h4 className={`font-semibold text-yellow-900 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('skippedRows')}
                    </h4>
                    <div className="max-h-48 overflow-y-auto border border-yellow-200 rounded">
                      {importResults.skipped.map((item, idx) => (
                        <div key={idx} className={`p-2 border-b border-yellow-100 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <p className="text-sm font-medium text-yellow-900">
                            {isRTL ? `صف ${item.row}:` : `Row ${item.row}:`} {item.reason}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    onSuccess();
                    onClose();
                  }}
                  className="flex-1"
                >
                  {t('ok') || 'OK'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportPostsModal;